import React, { useState, useRef, useCallback, useEffect } from "react";
import { Box, Typography, Button, IconButton, Popover } from "@mui/material";
import { CloseRounded, DragIndicatorRounded } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { pageScrollStyle } from "../styles/styles";

const FilterDrawer = ({
  open,
  onClose,
  onApply,
  onReset,
  title = "Filters",
  children,
  isFilterApplied = false,
  isLoading = false,
  minWidth = 320,
  maxWidth = 600,
  defaultWidth = 320,
  anchorEl = null,
  anchorOrigin = { vertical: "bottom", horizontal: "right" },
  transformOrigin = { vertical: "top", horizontal: "right" },
}) => {
  const [drawerWidth, setDrawerWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const isResizingRef = useRef(false);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizingRef.current) return;

      const deltaX = startXRef.current - e.clientX;
      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, startWidthRef.current + deltaX)
      );

      setDrawerWidth(newWidth);
    },
    [minWidth, maxWidth]
  );

  const handleMouseUp = useCallback(() => {
    isResizingRef.current = false;
    setIsResizing(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      isResizingRef.current = true;
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = drawerWidth;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [drawerWidth, handleMouseMove, handleMouseUp]
  );

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Reset width when drawer closes
  useEffect(() => {
    if (!open) {
      setDrawerWidth(defaultWidth);
      isResizingRef.current = false;
      setIsResizing(false);
    }
  }, [open, defaultWidth]);

  const handleApply = () => {
    onApply?.();
    onClose?.();
  };

  const handleReset = () => {
    onReset?.();
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      disableScrollLock
      sx={{
        "& .MuiPopover-paper": {
          marginTop: 1,
          marginBottom: 1,
        },
      }}
      PaperProps={{
        sx: {
          height: "auto",
          maxHeight: "80vh",
          minHeight: 200,
          width: drawerWidth,
          borderRadius: "1rem",
          transition: isResizing ? "none" : "width 0.2s ease-in-out",
          overflow: "auto",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          border: "1px solid",
          borderColor: "divider",
          ...pageScrollStyle,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "background.paper",
        }}
      >
        {/* Resize Handle */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "8px",
            cursor: "col-resize",
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
            "&:hover .resize-icon": {
              opacity: 1,
            },
            zIndex: 1300,
            userSelect: "none",
          }}
          onMouseDown={handleMouseDown}
        >
          <DragIndicatorRounded
            className="resize-icon"
            sx={{
              color: "primary.main",
              fontSize: "1.2rem",
              opacity: 0.5,
              transition: "opacity 0.2s",
              pointerEvents: "none",
            }}
          />
        </Box>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: "0.5rem",
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            flexShrink: 0,
            position: "sticky",
            backdropFilter: "blur(10px)",
            top: 0,
            zIndex: 99,
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontSize: "0.9rem", fontWeight: "500" }}
          >
            {title}
          </Typography>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 32,
              height: 32,
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
          }}
        >
          {children}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={handleReset}
            disabled={!isFilterApplied}
            sx={{ textTransform: "capitalize" }}
          >
            Reset
          </Button>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={handleApply}
            size="small"
            sx={{ textTransform: "capitalize" }}
          >
            Apply Filters
          </LoadingButton>
        </Box>
      </Box>
    </Popover>
  );
};

export default FilterDrawer;
